// Creative Detail Modal - Full creative details with actions
import React, { useState, useEffect } from 'react';
import { creativeLibraryApi } from '../../lib/creativeLibraryApi';
import type { Creative, CreativeUsageHistory } from '../../types/creative-library';
import { PerformanceTierBadge } from '../PerformanceTierBadge';
import { formatCurrency, formatNumber, formatPercent } from '../../lib/formatters';

interface CreativeDetailModalProps {
  creativeId: string;
  onClose: () => void;
  onReuse?: (creativeId: string) => void;
  onArchive?: (creativeId: string) => void;
}

export const CreativeDetailModal: React.FC<CreativeDetailModalProps> = ({
  creativeId,
  onClose,
  onReuse,
  onArchive
}) => {
  const [creative, setCreative] = useState<Creative & { usage_history: CreativeUsageHistory[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    loadCreative();
  }, [creativeId]);

  const loadCreative = async () => {
    try {
      setLoading(true);
      const data = await creativeLibraryApi.getCreative(creativeId);
      setCreative(data);
      setNotes(data.notes || '');
      setTags(data.tags || []);
    } catch (error) {
      console.error('Failed to load creative:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!creative) return;
    
    try {
      setSavingNotes(true);
      await creativeLibraryApi.updateNotes(creative.id, notes);
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim() || !creative) return;
    
    try {
      await creativeLibraryApi.addTags(creative.id, [newTag.trim()]);
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    if (!creative) return;
    
    try {
      await creativeLibraryApi.removeTag(creative.id, tag);
      setTags(tags.filter(t => t !== tag));
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleDownload = () => {
    if (!creative) return;
    window.open(creative.asset_url, '_blank');
  };

  const handleReuse = () => {
    if (!creative) return;
    onReuse?.(creative.id);
  };

  const handleArchive = async () => {
    if (!creative || !confirm('Archive this creative? It will no longer appear in the library.')) return;
    
    try {
      await creativeLibraryApi.archiveCreative(creative.id);
      onArchive?.(creative.id);
      onClose();
    } catch (error) {
      console.error('Failed to archive creative:', error);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-boxdark rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!creative) {
    return null;
  }

  const statusColors: Record<string, string> = {
    active: 'bg-success text-white',
    paused: 'bg-warning text-white',
    archived: 'bg-bodydark1 text-white'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-boxdark rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-boxdark border-b border-stroke dark:border-strokedark px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-black dark:text-white">
              {creative.name}
            </h2>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[creative.status]}`}>
              {creative.status.toUpperCase()}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-bodydark hover:text-black dark:text-bodydark1 dark:hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Left Column: Preview */}
          <div className="space-y-6">
            {/* Asset Preview */}
            <div className="bg-gray-100 dark:bg-meta-4 rounded-lg aspect-square flex items-center justify-center overflow-hidden">
              {creative.creative_type === 'video' ? (
                <video
                  src={creative.asset_url}
                  controls
                  className="max-w-full max-h-full"
                  poster={creative.thumbnail_url}
                />
              ) : (
                <img
                  src={creative.asset_url}
                  alt={creative.name}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>

            {/* Metadata */}
            <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-black dark:text-white">Metadata</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-bodydark dark:text-bodydark1">Platform:</span>
                  <span className="ml-2 font-medium text-black dark:text-white capitalize">{creative.platform}</span>
                </div>
                <div>
                  <span className="text-bodydark dark:text-bodydark1">Type:</span>
                  <span className="ml-2 font-medium text-black dark:text-white capitalize">{creative.creative_type}</span>
                </div>
                {creative.dimensions && (
                  <div>
                    <span className="text-bodydark dark:text-bodydark1">Dimensions:</span>
                    <span className="ml-2 font-medium text-black dark:text-white">
                      {creative.dimensions.width}x{creative.dimensions.height}
                    </span>
                  </div>
                )}
                {creative.duration_seconds && (
                  <div>
                    <span className="text-bodydark dark:text-bodydark1">Duration:</span>
                    <span className="ml-2 font-medium text-black dark:text-white">{creative.duration_seconds}s</span>
                  </div>
                )}
                {creative.file_size_mb && (
                  <div>
                    <span className="text-bodydark dark:text-bodydark1">File Size:</span>
                    <span className="ml-2 font-medium text-black dark:text-white">{creative.file_size_mb.toFixed(1)} MB</span>
                  </div>
                )}
                <div>
                  <span className="text-bodydark dark:text-bodydark1">Created:</span>
                  <span className="ml-2 font-medium text-black dark:text-white">
                    {new Date(creative.created_at).toLocaleDateString()}
                  </span>
                </div>
                {creative.last_used_at && (
                  <div>
                    <span className="text-bodydark dark:text-bodydark1">Last Used:</span>
                    <span className="ml-2 font-medium text-black dark:text-white">
                      {new Date(creative.last_used_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-bodydark dark:text-bodydark1">Used In:</span>
                  <span className="ml-2 font-medium text-black dark:text-white">
                    {creative.campaigns_used_in} campaigns
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Performance & Actions */}
          <div className="space-y-6">
            {/* Performance Tier */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-black dark:text-white">Performance</h3>
              <PerformanceTierBadge
                tier={creative.performance_tier}
                roas={creative.roas}
                totalCreatives={487}
              />
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4">
                <div className="text-bodydark dark:text-bodydark1 text-sm mb-1">Impressions</div>
                <div className="text-2xl font-bold text-black dark:text-white">{formatNumber(creative.impressions)}</div>
              </div>
              <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4">
                <div className="text-bodydark dark:text-bodydark1 text-sm mb-1">Clicks</div>
                <div className="text-2xl font-bold text-black dark:text-white">{formatNumber(creative.clicks)}</div>
                <div className="text-xs text-bodydark dark:text-bodydark1">CTR: {formatPercent(creative.ctr)}</div>
              </div>
              <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4">
                <div className="text-bodydark dark:text-bodydark1 text-sm mb-1">Conversions</div>
                <div className="text-2xl font-bold text-black dark:text-white">{formatNumber(creative.conversions)}</div>
                <div className="text-xs text-bodydark dark:text-bodydark1">CVR: {formatPercent(creative.cvr)}</div>
              </div>
              <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4">
                <div className="text-bodydark dark:text-bodydark1 text-sm mb-1">ROAS</div>
                <div className="text-2xl font-bold text-success">{creative.roas.toFixed(2)}x</div>
              </div>
              <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4">
                <div className="text-bodydark dark:text-bodydark1 text-sm mb-1">Spend</div>
                <div className="text-2xl font-bold text-black dark:text-white">{formatCurrency(creative.spend)}</div>
              </div>
              <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4">
                <div className="text-bodydark dark:text-bodydark1 text-sm mb-1">Revenue</div>
                <div className="text-2xl font-bold text-success">{formatCurrency(creative.revenue)}</div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <h3 className="font-semibold text-black dark:text-white mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary bg-opacity-10 text-primary rounded-full text-sm"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:bg-primary hover:bg-opacity-20 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  placeholder="Add tag..."
                  className="flex-1 px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent text-sm"
                />
                <button
                  onClick={handleAddTag}
                  disabled={!newTag.trim()}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Notes */}
            <div>
              <h3 className="font-semibold text-black dark:text-white mb-3">Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleSaveNotes}
                placeholder="Add notes about this creative..."
                className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent text-sm min-h-[100px] resize-y"
              />
              {savingNotes && (
                <div className="text-xs text-bodydark dark:text-bodydark1 mt-1">Saving...</div>
              )}
            </div>

            {/* Usage History */}
            {creative.usage_history && creative.usage_history.length > 0 && (
              <div>
                <h3 className="font-semibold text-black dark:text-white mb-3">
                  Usage History ({creative.usage_history.length} campaigns)
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {creative.usage_history.map((usage, idx) => (
                    <div key={idx} className="bg-gray-50 dark:bg-meta-4 rounded-lg p-3">
                      <div className="font-medium text-black dark:text-white text-sm">{usage.campaign_name}</div>
                      <div className="text-xs text-bodydark dark:text-bodydark1 mt-1">
                        {new Date(usage.date_added).toLocaleDateString()}
                        {usage.date_removed && ` - ${new Date(usage.date_removed).toLocaleDateString()}`}
                      </div>
                      <div className="text-xs text-bodydark dark:text-bodydark1 mt-1">
                        {formatNumber(usage.impressions)} imp • {formatNumber(usage.clicks)} clicks • {formatCurrency(usage.spend)} spent
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-stroke dark:border-strokedark">
              <button
                onClick={handleReuse}
                className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 font-medium transition-colors"
              >
                Reuse in Campaign
              </button>
              <button
                onClick={handleDownload}
                className="px-4 py-3 border border-stroke dark:border-strokedark rounded-lg hover:bg-gray-50 dark:hover:bg-meta-4 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              <button
                onClick={handleArchive}
                className="px-4 py-3 border border-danger text-danger rounded-lg hover:bg-danger hover:text-white transition-colors"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
